import { placeOrderApiClient } from "@api-client/place_order";
import type { ResType } from '@api-client/typeUtils';
import { formatDate } from "./formatDate";
import { estimatedTimeText1 } from "./estimatedTimeText";
import { formatCurrency } from "./formatCurrency";
// import { feeBreakdownNameMap } from "./calculateCartBreakdown";

const orderDetailsApiPath = placeOrderApiClient[":storeOrderId"].get_order.$post;
type OrderType = ResType<typeof orderDetailsApiPath>["data"]['order'];

/** Escape text embedded in thermal HTML so notes cannot break markup and wrap safely in &lt;pre&gt;. */
function escapeHtmlForPrinter(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const style = (
    fontSizeRem: number,
    fontWeight: 'normal' | 'bold' = 'normal'
) => {
    return `
<style>
    html, body {
        max-width: 100%;
        overflow-x: hidden;
    }
    body {
        padding: 0px;
        margin: 0px;
        font-size: ${fontSizeRem}rem;
        font-weight: ${fontWeight};
    }
    /* width:100% + horizontal margin overflows the page (100% + 30px). Use vertical margin + horizontal padding only. */
    pre {
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        margin: 15px 0;
        padding: 0 8px;
        overflow: hidden;
        font-family: monospace;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: anywhere;
        word-break: break-word;
    }
    /* Long strings with no spaces: break anywhere (thermal paper fixed width). */
    pre .order-note {
        word-break: break-all;
        overflow-wrap: anywhere;
    }
    pre h6 {
        margin: 0px;
        font-size: ${fontSizeRem}rem;
    }
    pre h5 {
        margin: 0px;
        font-size: ${fontSizeRem * 1.2}rem;
    }
    pre h4 {
        margin: 0px;
        font-size: ${fontSizeRem * 1.1}rem;
    }
    pre h3 {
        margin: 0px;
        font-size: ${fontSizeRem}rem;
    }
    pre table {
        width: 100%;
        font-size: inherit;
        font-weight: inherit;
    }
    pre .item-qty{
        font-size: ${fontSizeRem * 1.2}rem;
        font-weight: bold;
    }
    pre .bold{
        font-size: ${fontSizeRem * 1.2}rem;
        font-weight: bold;
    }
    pre .item-name{
        padding: ${(fontSizeRem * 0.4).toFixed(2)}rem;
        // border-left: 1px solid #000; 
        // border-right: 1px solid #000;
        font-family: monospace;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }
    hr {
        border: 1px dashed #000;
    }
</style>
`
}

export const thermalPrinterFormat = (
    orderDetails: OrderType,
    locale: string | undefined = undefined,
    fontSizeRem: number = 0.8,
    fontWeight: 'normal' | 'bold' = 'normal'
) => {
    if (!orderDetails) {
        throw new Error('Order details are required');
    }
    const orderedQuantity = () => {
        return orderDetails?.orderedItemsJSON?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
    };

    // Header
    let output = '';
    output += `${orderDetails.storeName}|${orderDetails.storeAddress1}`;
    output += `<h5 >#${orderDetails.shortOtp}</h5>`;
    output += `<hr>`;
    if (orderDetails.orderType === 'pickup') {
        output += `<h5 >${orderDetails.pickupNameSurname || orderDetails.userName || 'unknown'}</h5>`;
    } else if (orderDetails.orderType === 'delivery') {
        output += `<h5>${orderDetails.deliveryAddressJSON?.shippingName || orderDetails.pickupNameSurname || orderDetails.userName || 'unknown'}</h5>`;
    }
    output += `<hr>`;
    output += `<h5 >${orderDetails.orderType === 'delivery' ? 'DELIVERY' : 'PICKUP'}</h5>`;
    output += `<hr>`;

    output += `<h6>TOTAL: ${formatCurrency(orderDetails.finalAmount, orderDetails.taxSettingsJSON?.currency, locale)}</h6>`;
    output += `<hr>`;
    if (orderDetails.isCancelled) {
        output += `<h6>[CANCELLED]</h6>`;
        if (orderDetails?.paymentMethodJSON?.isOnline) {
            output += `<h6>[REFUNDED]</h6>`;
        }
    }
    if (orderDetails?.paymentMethodJSON?.isOnline) {
        output += `<h4>[PAID-ONLINE] ${orderDetails.paymentId}</h4>`;
    } else {
        output += `<h4>[NOT-PAID] ${orderDetails?.paymentMethodJSON?.name}</h4>`;
    }
    output += `<hr>`;

    // Delivery or Pickup details
    if (orderDetails.orderType === 'delivery' && orderDetails.deliveryAddressJSON) {
        output += `DELIVERY ADDRESS:\n`;
        output += `${orderDetails.deliveryAddressJSON.address1}\n`;
        if (orderDetails.deliveryAddressJSON.address2) {
            output += `${orderDetails.deliveryAddressJSON.address2}\n`;
        }
        // output += `${[orderDetails.deliveryAddressJSON.city, orderDetails.deliveryAddressJSON.state].filter(Boolean).join(', ')} ${orderDetails.deliveryAddressJSON.postalCode}\n`;
        if (orderDetails.deliveryAddressJSON.postalCode) {
            output += `${orderDetails.deliveryAddressJSON.postalCode}\n`;
        }
        if (orderDetails.deliveryAddressJSON.instructionsForPickupDelivery) {
            output += `Instructions: ${orderDetails.deliveryAddressJSON.instructionsForPickupDelivery}\n`;
        }
        if (orderDetails.deliveryAddressJSON.instructionCategory) {
            output += `${orderDetails.deliveryAddressJSON.instructionCategory}\n`;
        }
        if (orderDetails.deliveryAddressJSON.phoneNumber) {
            output += `${orderDetails.deliveryAddressJSON.phoneNumber}\n`;
        }
        output += `<hr>`;
    }

    if (orderDetails.userVerifiedPhoneNumbers) {
        output += `${orderDetails.userVerifiedPhoneNumbers}(verified)\n`;
    }
    output += `${formatDate(
        (orderDetails.scheduled_at_ts ? orderDetails.scheduled_at_ts : orderDetails.order_placed_at_ts) || Date.now(),
        orderDetails.storeTimezone || undefined,
        false
    )}\n`;
    if (orderDetails.scheduled_at_ts) {
        output += `[SCHEDULED]\n`;
    } else {
        output += `[ASAP]`;
    }
    if (orderDetails.estimatedTimeJSON) {
        output += `${estimatedTimeText1(orderDetails.orderType!, orderDetails.estimatedTimeJSON)}\n`;
    }
    output += `Order count: ${orderDetails.orderCount}`;
    output += `<hr>`;

    // Order Notes
    if (orderDetails.orderNote) {
        output += `NOTE: <span class="order-note">${escapeHtmlForPrinter(orderDetails.orderNote)}</span>`;
        output += `<hr>`;
    }

    const totalPercentagePromoDiscount = orderDetails.orderedItemsJSON?.reduce((total, item) => {
        return total + (item.hasPercentagePromo?.discount || 0);
    }, 0) || 0;

    if (orderDetails.orderedItemsJSON?.length) {
        output += `<h6 class="bold">ITEMS</h6>`;
        output += `<hr>`;
        output += `<table>`;

        orderDetails.orderedItemsJSON.forEach((item, index) => {
            // Main item row
            const nameAndDetails = item.details ? `${item.name}\n${item.details}` : item.name;
            output += `<tr>`;
            output += `<td class="item-qty">${item.quantity}x</td>`;
            output += `<td class="item-name">${nameAndDetails}</td>`;// white-space: pre-wrap;
            output += `<td style="text-align: right;">${item.shownFee}</td>`;
            output += `</tr>`;

            // Promo details row (if any)
            const promoDetails = [];
            if (item.hasBuyXGetYFreePromo) {
                promoDetails.push(`Buy${item.hasBuyXGetYFreePromo.buyQ},Get${item.hasBuyXGetYFreePromo.getQ}Free(-${formatCurrency(item.hasBuyXGetYFreePromo.discount, orderDetails.taxSettingsJSON?.currency, locale)})(${item.hasBuyXGetYFreePromo.freeItemsCount}X)`);
            }
            // if (item.hasPercentagePromo) {
            //     promoDetails.push(`-${item.hasPercentagePromo.percentage}% off(-${formatCurrency(item.hasPercentagePromo.discount, orderDetails.taxSettingsJSON?.currency, locale)})`);
            // }

            if (promoDetails.length > 0) {
                output += `<tr>`;
                output += `<td colspan="3" style="padding: 2px; font-style: italic;">${promoDetails.join('\n')}</td>`;
                output += `</tr>`;
            }

            if (index < orderDetails.orderedItemsJSON!.length - 1) {
                output += `<tr><td colspan="3"><hr></td></tr>`;
            }
        });

        output += `</table>`;
    }

    // Order Items
    output += `<hr>`;
    output += `Total item count:${orderedQuantity()}\n`;
    output += `<hr>`;
    if (totalPercentagePromoDiscount > 0) {
        output += `Percentage Discounts: -${formatCurrency(totalPercentagePromoDiscount, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
    }
    if (orderDetails.cartTotalsJSON) {
        output += `Subtotal: ${formatCurrency(orderDetails.cartTotalsJSON.shownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
    }

    if (orderDetails.subtotalJSON?.bestDeliveryZone) {
        output += `Delivery Fee: ${formatCurrency(orderDetails.subtotalJSON.bestDeliveryZone.shownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
    }

    if (orderDetails.subtotalJSON?.calculatedCustomServiceFees?.length) {
        orderDetails.subtotalJSON.calculatedCustomServiceFees.forEach(fee => {
            if (fee.shownFee > 0) {
                output += `${fee.name}: ${formatCurrency(fee.shownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
            }
        });
    }

    // other fees
    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesShownFee > 0) {
        output += `${orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesName}: ${formatCurrency(orderDetails.cartBreakdownJSON.buyerPaysTaxAndFeesShownFee, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
    }

    // tax and service fee details
    // if (orderDetails.cartBreakdownJSON?.buyerPaysTaxAndFees?.length) {
    //     orderDetails.cartBreakdownJSON?.buyerPaysTaxAndFees.forEach(fee => {
    //         output += `${feeBreakdownNameMap[fee.type].name}: ${fee.currencyShownFee}\n`;
    //     });
    // }

    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.discount > 0) {
        output += `Discount: -${formatCurrency(orderDetails.cartBreakdownJSON.discount, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
    }
    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.firstOrdersDiscount > 0) {
        output += `First Orders Promo: -${formatCurrency(orderDetails.cartBreakdownJSON.firstOrdersDiscount, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
    }

    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.usedCredit > 0) {
        output += `Used Store Credit: -${formatCurrency(orderDetails.cartBreakdownJSON.usedCredit, orderDetails.taxSettingsJSON?.currency, locale)}\n`;
    }

    if (orderDetails.cartBreakdownJSON && orderDetails.cartBreakdownJSON.earnCreditAmountByOrdering > 0) {
        output += `Gift credit: ${formatCurrency(orderDetails.cartBreakdownJSON?.earnCreditAmountByOrdering, orderDetails.taxSettingsJSON?.currency)}\n`;
    }

    output += `<hr>`;
    output += `<h6>TOTAL: ${formatCurrency(orderDetails.finalAmount, orderDetails.taxSettingsJSON?.currency, locale)}</h6>`;
    output += `<hr>`;
    if (orderDetails?.paymentMethodJSON?.isOnline) {
        output += `<h4>[PAID-ONLINE] ${orderDetails.paymentId}</h4>`;
    } else {
        output += `<h4>[NOT-PAID] ${orderDetails?.paymentMethodJSON?.name}</h4>`;
    }
    output += `<hr>`;


    return `<html><body>${style(fontSizeRem, fontWeight)}<pre>${output}</pre></body></html>`;
}
